// utils/cropImage.ts
export type AreaPixels = { x: number; y: number; width: number; height: number }

const OUTPUT = 1080

export async function cropToBase64(
  src: string,
  area: AreaPixels,
  rotation: number,
  {
    quality = 0.9,
    type = 'image/jpeg',
    filter = '', // e.g. "brightness(1.05) contrast(1.1) saturate(1.2)"
  }: { quality?: number; type?: string; filter?: string } = {},
): Promise<string> {
  if (!area || area.width <= 0 || area.height <= 0) {
    throw new Error('Empty crop area')
  }
  const img = await loadImage(src)
  if (!img.width || !img.height) {
    throw new Error('Image has no intrinsic size')
  }

  const rads = degToRad(rotation)
  const { width: bW, height: bH } = rotateSize(img.width, img.height, rads)
  if (!bW || !bH) throw new Error('Invalid buffer size')

  // 1) draw ROTATED + FILTERED image to a buffer
  const buffer = document.createElement('canvas')
  buffer.width = bW
  buffer.height = bH
  const bctx = buffer.getContext('2d')
  if (!bctx) throw new Error('No 2D context')

  bctx.translate(bW / 2, bH / 2)
  bctx.rotate(rads)
  // ⬇️ apply filters to drawImage
  // Supported: brightness/contrast/saturate/sepia/grayscale/hue-rotate/blur(...) etc.
  bctx.filter = filter || 'none'
  bctx.drawImage(img, -img.width / 2, -img.height / 2)
  bctx.setTransform(1, 0, 0, 1, 0, 0)

  // 2) crop requested rect
  const cropCanvas = document.createElement('canvas')
  cropCanvas.width = area.width
  cropCanvas.height = area.height
  const cropCtx = cropCanvas.getContext('2d')!
  cropCtx.drawImage(buffer, area.x, area.y, area.width, area.height, 0, 0, area.width, area.height)

  // 3) scale to 1080×1080
  const out = document.createElement('canvas')
  out.width = OUTPUT
  out.height = OUTPUT
  const octx = out.getContext('2d')!
  octx.imageSmoothingEnabled = true
  ;(octx as any).imageSmoothingQuality = 'high'
  octx.drawImage(cropCanvas, 0, 0, OUTPUT, OUTPUT)

  return out.toDataURL(type, quality)
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = async () => {
      try {
        if ('decode' in img) {
          await (img as any).decode()
        }
        resolve(img)
      } catch {
        resolve(img)
      }
    }
    img.onerror = reject
    img.src = src
    // si déjà en cache
    if (img.complete && img.naturalWidth) {
      resolve(img)
    }
  })
}

function degToRad(d: number) {
  return (d * Math.PI) / 180
}
function rotateSize(w: number, h: number, rads: number) {
  const cos = Math.abs(Math.cos(rads))
  const sin = Math.abs(Math.sin(rads))
  return { width: Math.floor(w * cos + h * sin), height: Math.floor(w * sin + h * cos) }
}
